import React, { useMemo, useCallback, useReducer, useEffect, useContext } from 'react';
import { Form } from '../ui-kit';
import { xor, without, intersection, isEmpty, union, sortBy, isEqual } from 'lodash';
import { usePrevious } from '../../utils/custom-hooks';

import Loader from '../general/Loader';

const Context = React.createContext(null);

const reducer = (state, action) => {
  switch (action.type) {
    case 'toggleGroup': {
      return toggleGroup(state, action.payload.group, action.payload.attribute);
    }
    case 'toggleItem': {
      return toggleItem(state, action.payload.item);
    }
    default: {
      throw new Error('unknown action');
    }
  }
};

const isGroupSelected = (group, selected, attribute) =>
  !isEmpty(
    intersection(
      selected,
      group[attribute].map(item => item.id)
    )
  );

const toggleGroup = (state, group, attribute) => {
  if (isGroupSelected(group, state.selected, attribute)) {
    // De-select all group's items
    return {
      ...state,
      selected: without(state.selected, ...group[attribute].map(item => item.id))
    };
  }

  // Select all group's items
  return {
    ...state,
    selected: union(
      state.selected,
      group[attribute].map(item => item.id)
    )
  };
};

const toggleItem = (state, item) => {
  return {
    ...state,
    selected: xor(state.selected, [item.id])
  };
};

const GroupTreeElement = ({ group, childNodes }) => {
  const { state, dispatch, attribute } = useContext(Context);

  const handleGroupChange = useCallback(
    group => {
      dispatch({ type: 'toggleGroup', payload: { group, attribute } });
    },
    [dispatch, attribute]
  );

  const handleItemChange = useCallback(
    item => {
      dispatch({ type: 'toggleItem', payload: { item, attribute } });
    },
    [dispatch, attribute]
  );

  if (!group) return null;
  if (group.type === 'team') return null;

  childNodes = sortBy(Object.values(childNodes || {}), d => d.data.name);

  return (
    <div className="ms-3">
      {!isEmpty(group[attribute]) && (
        <Form.Check
          id={`select_all_group_${group.id}`}
          checked={isGroupSelected(group, state.selected, attribute)}
          onChange={() => handleGroupChange(group)}
          label={group.name}
        />
      )}
      {group.type === 'station' &&
        !isEmpty(group[attribute]) &&
        group[attribute].map(item => (
          <Form.Check
            id={`select_item_${item.id}`}
            key={item.id}
            className="ms-3"
            checked={state.selected.includes(item.id)}
            onChange={() => handleItemChange(item)}
            label={item.name}
          />
        ))}
      {childNodes.map(subTree => (
        <GroupTreeElement key={subTree.data.id} group={subTree.data} childNodes={subTree.children} />
      ))}
    </div>
  );
};

// Walks the tree until parent's subtree is found
const findParentScopedTrees = (tree, parents) => {
  if (Array.isArray(parents)) {
    return parents.flatMap(parentId => findParentScopedTrees(tree, parentId));
  }

  const parent = parents;
  const id = typeof parent === 'object' ? parent.id : parent;

  if (tree[id]) return [{ [id]: tree[id] }];

  let found = null;
  for (const subTree of Object.values(tree)) {
    if (Object.entries(subTree.children).length > 0) {
      found = findParentScopedTrees(subTree.children, parents);
      if (found.length > 0) break;
    }
  }

  return [found].filter(Boolean).flat();
};

const HierarchicalGroupSelector = ({ groupsTree, parentGroupId, attribute, selected, onChange, label }) => {
  // useMemo memoizes the expensive tree calculation for every re-render
  const scopedTrees = useMemo(() => {
    if (parentGroupId) return findParentScopedTrees(groupsTree, parentGroupId);
    return [groupsTree];
  }, [groupsTree, parentGroupId]);

  // Using a reducer to ensure states changes always happen top down
  const [state, dispatch] = useReducer(reducer, { selected });

  const previousState = usePrevious(state);

  // Notify onChange to the parent component on every state change
  useEffect(() => {
    if (isEqual(previousState, state) || !onChange) return;

    onChange(state.selected);
  }, [state, onChange, previousState]);

  if (isEmpty(groupsTree)) return <Loader />;

  const entries = scopedTrees.map(t => Object.values(t)[0]);

  return (
    <Context.Provider value={{ attribute, state, dispatch }}>
      <Form.Group>
        <label className="mb-2">{label}</label>
        {entries.map(({ data: group, children: childNodes }) => (
          <GroupTreeElement key={group.id} group={group} childNodes={childNodes} />
        ))}
      </Form.Group>
    </Context.Provider>
  );
};

export default HierarchicalGroupSelector;
